import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { InviteForm } from '@/components/team/InviteForm';
import { MembersList } from '@/components/team/MembersList';
import { AccessRequestsList } from '@/components/team/AccessRequestsList';
import { SeatUsageIndicator } from '@/components/team/SeatUsageIndicator';
import { WorkspaceMessages } from '@/components/team/WorkspaceMessages';
import { getWorkspaceMembers, getPendingInvites, WorkspaceMember, PendingInvite } from '@/lib/api/workspaces';
import { supabase } from '@/integrations/supabase/client';
import { Users, UserPlus, Loader2, Info, MessageSquare } from 'lucide-react';

interface TeamManagementSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  userId: string;
  defaultTab?: string;
  defaultRecipientId?: string;
}

export const TeamManagementSheet = ({ open, onOpenChange, workspaceId, userId, defaultTab = 'members', defaultRecipientId }: TeamManagementSheetProps) => {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [localRecipientId, setLocalRecipientId] = useState<string | undefined>(defaultRecipientId);
  
  const currentUserMember = members.find(m => m.user_id === userId);
  const currentUserRole = currentUserMember?.role;

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersData, invitesData, { data: workspaceData }] = await Promise.all([
        getWorkspaceMembers(workspaceId),
        getPendingInvites(workspaceId),
        supabase.from('workspaces').select('*').eq('id', workspaceId).single(),
      ]);

      setMembers(membersData);
      setInvites(invitesData);
      setWorkspace(workspaceData);
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && workspaceId) {
      loadData();
      setActiveTab(defaultTab);
      setLocalRecipientId(defaultRecipientId);
    }
  }, [open, workspaceId, defaultTab, defaultRecipientId]);

  const activeMembers = members.filter(m => m.seat_status === 'active');
  const usedSeats = activeMembers.length + invites.length;
  const maxSeats = workspace?.max_seats || 5;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto border-border bg-background">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <SheetTitle className="text-lg font-semibold">
                  Workspace-Team
                </SheetTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px]">
                    <p className="text-xs">Hier verwaltest du Workspace-Seats und Einladungen. Teilnehmer pro Entscheidung werden direkt in der jeweiligen Session zugewiesen.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <SheetDescription className="text-sm">
                Seats und Einladungen verwalten
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Wird geladen...</p>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            <SeatUsageIndicator
              usedSeats={usedSeats}
              maxSeats={maxSeats}
              activeMembers={activeMembers.length}
              pendingInvites={invites.length}
            />

            {/* Access Requests (only visible to admins/owners) */}
            {(currentUserRole === 'owner' || currentUserRole === 'admin') && (
              <AccessRequestsList
                workspaceId={workspaceId}
                onRefresh={loadData}
                onMessageUser={(uid) => {
                  setLocalRecipientId(uid);
                  setActiveTab('messages');
                }}
              />
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 h-10">
                <TabsTrigger value="members" className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4" />
                  Team ({activeMembers.length})
                </TabsTrigger>
                <TabsTrigger value="messages" className="flex items-center gap-2 text-sm">
                  <MessageSquare className="h-4 w-4" />
                  Nachrichten
                </TabsTrigger>
                <TabsTrigger value="invite" className="flex items-center gap-2 text-sm">
                  <UserPlus className="h-4 w-4" />
                  Einladen
                </TabsTrigger>
              </TabsList>

              <TabsContent value="members" className="mt-4">
                <MembersList 
                  members={members} 
                  invites={invites} 
                  currentUserRole={currentUserRole}
                  onRefresh={loadData}
                  onMessageUser={(uid) => {
                    setLocalRecipientId(uid);
                    setActiveTab('messages');
                  }}
                />
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <WorkspaceMessages
                  workspaceId={workspaceId}
                  currentUserRole={currentUserRole}
                  preselectedRecipientId={localRecipientId}
                />
              </TabsContent>

              <TabsContent value="invite" className="mt-4">
                <InviteForm
                  workspaceId={workspaceId}
                  userId={userId}
                  onInviteCreated={loadData}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
